import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu'
import { Portal } from '@chakra-ui/portal'
import { Icon, useClipboard, useDisclosure, Tooltip } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineCopy, AiOutlineInfoCircle } from 'react-icons/ai'
import { BsTags } from 'react-icons/bs'
import { CgPlayListAdd, CgPlayListRemove, CgRename } from 'react-icons/cg'
import { GrImage } from 'react-icons/gr'
import {
  HiOutlineDocumentDownload,
  HiOutlineFolderAdd,
  HiOutlineFolderRemove,
} from 'react-icons/hi'
import { IoImagesOutline, IoMoveSharp } from 'react-icons/io5'
import type { IconType } from 'react-icons/lib'
import {
  MdCenterFocusWeak,
  MdContentCopy,
  MdRestore,
  MdOutlineShare,
  MdAddLink,
  MdOutlineAddLink,
  MdPublishedWithChanges,
} from 'react-icons/md'
import { RiDeleteBin2Line, RiInboxArchiveLine } from 'react-icons/ri'
import { ImEmbed } from 'react-icons/im'
import { TiTags } from 'react-icons/ti'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import styled from 'styled-components'
import {
  allowExtensionOperations,
  downloadFileFromUrl,
  getFileURL,
  isEditableImage,
} from '../../../../utils/mediaLibrary'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import useToast from '../../../hooks/useToast'
import type {
  CopyMoveOperations,
  TagOperations,
  UserSessionResponse,
  ViewableParentSuccessResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import type { MediaLibraryActionType } from '../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import AutoTagsModal from '../Details/AutoTagsModal'
import RemoveBgModal from '../Details/RemoveBgModal'
import {
  CopyMoveDrawer,
  CustomFoucsAreaModal,
  DeleteMediaModal,
  DownloadModal,
  RenameFileModal,
  RenameFolderModal,
  RenameMediaCollectionModal,
  TagsModal,
  ShareAssetModal,
  ShareMediaCollectionModal,
} from '../MediaOperations'
import DeleteFileVersionModal from '../MediaOperations/DeleteFileVersionModal'
import RestoreFileVersionModal from '../MediaOperations/RestoreFileVersionModal'
import PublishFilesModal from '../MediaOperations/PublishFilesModal'
import PhotoEditor from '../PhotoEditor'
import {
  checkOperationPermission,
  checkOperationDisabled,
  checkPermission,
  PermissionsEnum,
} from '../../../../utils'
import {
  DeleteMediaCollectionModal,
  AddToMediaCollection,
  RemoveAssetFromMultipleMediaCollections,
  RemoveMultipleAssetsFromMediaCollection,
} from '../../MediaCollections'
import { ACLPermissionEnum } from '../../../constants'
import { FeaturesEnum } from '../../../../utils/features'
import { isRestrictedUser } from '../../../../utils/roles'
import {
  addLeadingSlash,
  addTrailingSlash,
  removeLeadingSlash,
  removeTrailingSlash,
} from '../../../../utils/urlHelpers'
import PublicLinksModal from '../MediaOperations/PublicLinksModal'
import FolderDetailsDrawer from '../MediaOperations/FolderDetailDrawer'
import MediaCollectionDetailDrawer from '../MediaOperations/MediaCollectionDetailDrawer'
import CopyEmbedURLModal from '../CopyEmbedURLModal'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'

interface ContextMenuItem {
  name: string
  action: MediaLibraryActionType
  viewConditions: boolean
  icon: IconType
  trigger(): void
  optionDisabledInfo?():
    | {
      isDisabled: boolean
      remainingLength?: Number
      assetName?: String
      tooltipMessage?: ReactElement
    }
    | undefined
}

const ContextMenu = (): ReactElement => {
  const [
    {
      contextMenu,
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      currentMediaCollection,
      folderPath,
      viewableParent,
      selectedBreadcrumbFolder,
      selectedBreadcrumbMediaCollection,
      refetchFiles,
    },
    dispatch,
  ] = useContext(MLContext)
  const {
    isPublicLink,
    urlPrefix,
    clientInfo,
    dashboardServerURL: publicLinkDashboardServerURL,
  } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const dashboardServerURL =
    user?.dashboardServerURL ?? publicLinkDashboardServerURL
  const history = useHistory()
  const toast = useToast()
  const { t } = useTranslation()
  const ASSET_IDS_COUNT_LIMIT = 100
  const [openEmbedModal, handleEmbedModal] = useState(false)

  const [tagOperation, setTagOperation] = useState<TagOperations>('updateTags')
  const [copyMoveOperation, setCopyMoveOperation] =
    useState<CopyMoveOperations>('copy')

  const {
    isOpen: isOpenAddToMediaCollection,
    onOpen: onOpenAddToMediaCollection,
    onClose: onCloseAddToMediaCollection,
  } = useDisclosure()

  const {
    isOpen: isOpenRenameFileModal,
    onOpen: onOpenRenameFileModal,
    onClose: onCloseRenameFileModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRenameFolderModal,
    onOpen: onOpenRenameFolderModal,
    onClose: onCloseRenameFolderModal,
  } = useDisclosure()

  const {
    isOpen: isOpenPublishFilesModal,
    onOpen: onOpenPublishFilesModal,
    onClose: onClosePublishFilesModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDownloadModal,
    onOpen: onOpenDownloadModal,
    onClose: onCloseDownloadModal,
  } = useDisclosure()

  const {
    isOpen: isOpenTagsModal,
    onOpen: onOpenTagsModal,
    onClose: onCloseTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAutoTagsModal,
    onOpen: onOpenAutoTagsModal,
    onClose: onCloseAutoTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveAssetFromMultipleMediaCollections,
    onOpen: onOpenRemoveAssetFromMultipleMediaCollections,
    onClose: onCloseRemoveAssetFromMultipleMediaCollections,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveMultipleAssetsFromMediaCollection,
    onOpen: onOpenRemoveMultipleAssetsFromMediaCollection,
    onClose: onCloseRemoveMultipleAssetsFromMediaCollection,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteMediaModal,
    onOpen: onOpenDeleteMediaModal,
    onClose: onCloseDeleteMediaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteFileVersionModal,
    onOpen: onOpenDeleteFileVersionModal,
    onClose: onCloseDeleteFileVersionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRestoreFileVersionModal,
    onOpen: onOpenRestoreFileVersionModal,
    onClose: onCloseRestoreFileVersionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenCopyMoveDrawer,
    onOpen: onOpenCopyMoveDrawer,
    onClose: onCloseCopyMoveDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenPhotoEditor,
    onOpen: onOpenPhotoEditor,
    onClose: onClosePhotoEditor,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveBgModal,
    onOpen: onOpenRemoveBgModal,
    onClose: onCloseRemoveBgModal,
  } = useDisclosure()

  const {
    isOpen: isOpenCustomFoucsAreaModal,
    onOpen: onOpenCustomFoucsAreaModal,
    onClose: onCloseCustomFoucsAreaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteMediaCollectionModal,
    onOpen: onOpenDeleteMediaCollectionModal,
    onClose: onCloseDeleteMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenPublicLinksModal,
    onOpen: onOpenPublicLinksModal,
    onClose: onClosePublicLinkModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRenameMediaCollectionModal,
    onOpen: onOpenRenameMediaCollectionModal,
    onClose: onCloseRenameMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenFolderDetailsDrawer,
    onOpen: onOpenFolderDetailsDrawer,
    onClose: onCloseFolderDetailsDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenCollectionDetailsDrawer,
    onOpen: onOpenCollectionDetailsDrawer,
    onClose: onCloseCollectionDetailsDrawer,
  } = useDisclosure()

  const [urlToCopy, setUrlToCopy] = useState<string>('')

  const { onCopy } = useClipboard(urlToCopy)
  const copyUrl = useRef<boolean>(false)
  const { request } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    if (copyUrl.current && urlToCopy) {
      onCopy()
      toast({
        description: t('copied_url_to_clipboard', 'Copied URL to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      copyUrl.current = false
      setUrlToCopy('')
    }
  }, [urlToCopy])

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo: clientInfo,
    user,
  })

  const contextMenuItemList = useMemo<ContextMenuItem[]>(() => {
    const isFileVersionSelected =
      selectedFiles.filter(item => item.type === 'file-version').length > 0
    return [
      {
        name: 'Copy',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: AiOutlineCopy,
        trigger: () => {
          dispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
              payload: '',
            },
          ])
          setCopyMoveOperation('copy')
          onOpenCopyMoveDrawer()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles, selectedFolders }
      },
      {
        name: 'Move',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: IoMoveSharp,
        trigger: () => {
          dispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
              payload: '',
            },
          ])
          setCopyMoveOperation('move')
          onOpenCopyMoveDrawer()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles, selectedFolders }
      },
      {
        name: 'Rename',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: CgRename,
        trigger: onOpenRenameFileModal,

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Rename',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: CgRename,
        trigger: onOpenRenameMediaCollectionModal,

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles, selectedFolders }
      },
      {
        name: 'Rename',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 1 &&
          selectedMediaCollections.length === 0 &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: CgRename,
        trigger: onOpenRenameFolderModal,

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: t('change_publication_status'),
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.DRAFT_ASSETS,
          ) ??
            false) &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: MdPublishedWithChanges,
        trigger: onOpenPublishFilesModal,
        optionDisabledInfo: () => {
          if (selectedFiles.length > ASSET_IDS_COUNT_LIMIT) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans t={t} i18nKey="publish_selection_limit_exceeded">
                  You cannot publish or unpublish more than{' '}
                  {{ ASSET_IDS_COUNT_LIMIT }} files at once.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Details',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          selectedFiles.length === 1 && selectedFolders.length === 0,
        icon: AiOutlineInfoCircle,
        trigger: () => {
          history.push(`/media-library/detail/${selectedFiles[0].fileId}`)
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Folder details',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: AiOutlineInfoCircle,
        trigger: onOpenFolderDetailsDrawer,
      },
      {
        name: 'Collection details',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          selectedMediaCollections.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: AiOutlineInfoCircle,
        trigger: onOpenCollectionDetailsDrawer,
      },
      {
        name: 'Add to media collection',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          (selectedFiles.length > 0 || selectedFolders.length > 0) &&
          selectedMediaCollections.length === 0 &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: HiOutlineFolderAdd,
        trigger: onOpenAddToMediaCollection,
        optionDisabledInfo: () => {
          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="add_to_media_collection_asset_limit_exceeded"
                >
                  You cannot add more than {{ ASSET_IDS_COUNT_LIMIT }} assets at
                  once to media collections.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Remove from media collections',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          [...selectedFiles, ...selectedFolders].length === 1 &&
          selectedMediaCollections.length === 0 &&
          currentMediaCollection === undefined,
        icon: HiOutlineFolderRemove,
        trigger: onOpenRemoveAssetFromMultipleMediaCollections,
      },
      {
        name: 'Remove from media collection',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          (selectedFiles.length > 0 || selectedFolders.length > 0) &&
          selectedMediaCollections.length === 0 &&
          currentMediaCollection !== undefined &&
          folderPath === '/' &&
          (isRestrictedUser(user?.roles)
            ? (viewableParent as ViewableParentSuccessResponse).mediaCollection
              ?.permission === ACLPermissionEnum.MANAGE
            : true),
        icon: HiOutlineFolderRemove,
        trigger: onOpenRemoveMultipleAssetsFromMediaCollection,
        optionDisabledInfo: () => {
          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="remove_from_media_collection_asset_limit_exceeded"
                >
                  You cannot remove more than {{ ASSET_IDS_COUNT_LIMIT }} assets
                  at once from media collections.
                </Trans>
              ),
            }
          }
        },
      },
      // Share asset - file/folder
      {
        name: 'Share',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          ((selectedFiles.length === 1 && selectedFolders.length === 0) ||
            (selectedFolders.length === 1 && selectedFiles.length === 0)) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineShare,
        trigger: () => {
          onOpenShareAssetModal()
        },
      },
      // Share media collection
      {
        name: 'Share',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          selectedMediaCollections.length === 1 &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineShare,
        trigger: () => {
          onOpenShareMediaCollectionModal()
        },
      },
      {
        name: 'Public links',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.PUBLIC_LINKS,
          ) ?? false) &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          (
            (selectedFiles.length + selectedFolders.length) === 1 ||
            selectedMediaCollections.length === 1
          ) &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineAddLink,
        trigger: onOpenPublicLinksModal,
      },
      {
        name: 'Download',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected,
        icon: HiOutlineDocumentDownload,
        trigger: () => {
          const downloadURL = `${removeTrailingSlash(dashboardServerURL!)}/${removeLeadingSlash(urlPrefix)}api/v2/files/${selectedFiles[0].fileId}/download?fileName=${selectedFiles[0].name}`
          downloadFileFromUrl(downloadURL)
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Download (as zip)',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isFileVersionSelected &&
          (selectedFiles.length > 1 ||
            selectedFolders.length > 0 ||
            selectedMediaCollections.length > 0) &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: RiInboxArchiveLine,
        trigger: () => {
          onOpenDownloadModal()
        },
      },
      {
        name: 'Copy URL',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          selectedFiles.length === 1 && selectedFolders.length === 0,
        icon: MdContentCopy,
        trigger: () => {
          copyUrl.current = true
          const toastRef = toast({
            description: t('generating_url_to_copy', 'Generating URL to copy'),
            status: 'info',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
          if (toastRef) {
            void getFileURL({
              file: selectedFiles[0],
              ...getFileUrlDynamicArguments,
              request,
            })
              .then(data => {
                if (data.includes('ik-s=')) {
                  if (data.includes('ik-auth-version')) {
                    const url = new URL(data)
                    url.searchParams.delete('ik-auth-version')
                    url.searchParams.delete('ik-s')
                    url.searchParams.delete('ik-t')
                    setUrlToCopy(url.toString())
                  } else {
                    setUrlToCopy(data)
                  }
                } else {
                  setUrlToCopy(data)
                }
                toast.close(toastRef)
              })
              .catch(() => {
                toast.update(toastRef, {
                  description: t(
                    'generating_url_to_copy_error',
                    'There was an error in generating the URL',
                  ),
                  status: 'error',
                })
              })
          }
        },
      },
      {
        name: 'Embed',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions: !isPublicLink && selectedFiles.length === 1 && selectedFolders.length === 0 && selectedFiles[0]?.mime?.includes('video') && selectedFiles[0]?.isPublished,
        icon: ImEmbed,
        trigger: () => {
          handleEmbedModal(true)
        },
      },
      {
        name: 'Image editor',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          isEditableImage(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: GrImage,
        trigger: onOpenPhotoEditor,

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Remove background',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          allowExtensionOperations(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: IoImagesOutline,
        trigger: onOpenRemoveBgModal,

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Custom focus area',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          isEditableImage(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdCenterFocusWeak,
        trigger: () => {
          onOpenCustomFoucsAreaModal()
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Tags',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: BsTags,
        trigger: () => {
          setTagOperation('updateTags')
          onOpenTagsModal()
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles }
      },
      {
        name: 'Add auto tags',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          allowExtensionOperations(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: TiTags,
        trigger: () => {
          onOpenAutoTagsModal()
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles }
      },
      {
        name: 'Add bulk tags',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: CgPlayListAdd,
        trigger: () => {
          setTagOperation('addTagsBulk')
          onOpenTagsModal()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles }
      },
      {
        name: 'Remove bulk tags',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: CgPlayListRemove,
        trigger: () => {
          setTagOperation('removeTagsBulk')
          onOpenTagsModal()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Restore version',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdRestore,
        trigger: () => {
          onOpenRestoreFileVersionModal()
        },
      },
      {
        name: 'Delete version',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: RiDeleteBin2Line,
        trigger: () => {
          onOpenDeleteFileVersionModal()
        },
      },
      {
        name: 'Delete',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: RiDeleteBin2Line,
        trigger: onOpenDeleteMediaModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles, selectedFolders }
      },
      {
        name: 'Delete',
        action: { type: MediaLibraryActions.CLOSE_CONTEXT_MENU },
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: RiDeleteBin2Line,
        trigger: onOpenDeleteMediaCollectionModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: { selectedFiles, selectedFolders }
      },
    ]
  }, [
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    user,
    dashboardServerURL,
    urlPrefix,
    currentMediaCollection,
    folderPath,
  ])

  const contextMenuViewCondition = useMemo<boolean>(
    () =>
      contextMenuItemList.reduce<boolean>(
        (previousItem, currentItem) =>
          previousItem || currentItem.viewConditions,
        false,
      ),
    [contextMenuItemList],
  )

  // custom style is being applied because chakra is not
  // behaving expected when coordinates of top is multiple of 16.
  const MenuButtonWrapper = styled(MenuButton) <{
    left: string
    top: string
  }>`
    aria-hidden: true;
    h: 1px;
    w: 1px;
    position: absolute;
    left: ${props => props.left}px;
    top: ${props => props.top}px;
    cursor: default;
    zindex: popover;
  `

  return (
    <>
      {contextMenu.visible && contextMenuViewCondition && (
        <Portal>
          <Menu
            isOpen={contextMenu.visible}
            gutter={0}
            onClose={() => {
              dispatch({ type: MediaLibraryActions.CLOSE_CONTEXT_MENU })
            }}
            placement={
              selectedBreadcrumbFolder || selectedBreadcrumbMediaCollection
                ? 'bottom'
                : 'auto'
            }
            isLazy
          >
            <MenuButtonWrapper
              left={contextMenu.position.x}
              top={contextMenu.position.y}
            />
            <MenuList zIndex="popover">
              {contextMenuItemList
                .filter(x => x.viewConditions)
                .map(x => {
                  const optionDisabledInfo = x.optionDisabledInfo
                    ? x.optionDisabledInfo()
                    : undefined
                  const assetName = optionDisabledInfo?.assetName
                  const remainingLength = optionDisabledInfo?.remainingLength
                  let tooltipLabel
                  if (optionDisabledInfo?.tooltipMessage) {
                    tooltipLabel = optionDisabledInfo.tooltipMessage
                  } else if (remainingLength === 0) {
                    tooltipLabel = (
                      <Trans
                        t={t}
                        i18nKey="context_menu_disable_tooltip_one_asset"
                      >
                        You do not have sufficient permissions to perform this
                        action on {{ assetName }}.
                      </Trans>
                    )
                  } else {
                    tooltipLabel = (
                      <Trans t={t} i18nKey="context_menu_disable_tooltip">
                        You do not have sufficient permissions to perform this
                        action on {{ assetName }} and {{ remainingLength }} more
                        items.
                      </Trans>
                    )
                  }

                  return (
                    <Tooltip
                      isDisabled={!optionDisabledInfo?.isDisabled}
                      label={tooltipLabel}
                      shouldWrapChildren
                      placement="right-end"
                      hasArrow
                      key={x.name}
                    >
                      <MenuItem
                        key={x.name}
                        color={
                          x.name === 'Delete' || x.name === 'Delete version'
                            ? 'appBrick'
                            : 'auto'
                        }
                        icon={
                          <Icon
                            as={x.icon}
                            boxSize="1rem"
                            marginTop="0.25rem"
                          />
                        }
                        onClick={() => {
                          dispatch(x.action)
                          x.trigger()
                        }}
                        isDisabled={Boolean(optionDisabledInfo?.isDisabled)}
                        isFocusable={true}
                      >
                        {x.name}
                      </MenuItem>
                    </Tooltip>
                  )
                })}
            </MenuList>
          </Menu>
        </Portal>
      )}

      {/* Modals wrapped with isOpen condition */}

      <AddToMediaCollection
        isOpen={isOpenAddToMediaCollection}
        onClose={onCloseAddToMediaCollection}
        onOpen={onOpenAddToMediaCollection}
      />

      {isOpenRemoveAssetFromMultipleMediaCollections && (
        <RemoveAssetFromMultipleMediaCollections
          isOpen={isOpenRemoveAssetFromMultipleMediaCollections}
          onClose={onCloseRemoveAssetFromMultipleMediaCollections}
        />
      )}

      {isOpenRemoveMultipleAssetsFromMediaCollection && (
        <RemoveMultipleAssetsFromMediaCollection
          isOpen={isOpenRemoveMultipleAssetsFromMediaCollection}
          onClose={onCloseRemoveMultipleAssetsFromMediaCollection}
        />
      )}

      {isOpenRenameFileModal && (
        <RenameFileModal
          isOpen={isOpenRenameFileModal}
          onClose={onCloseRenameFileModal}
        />
      )}
      {isOpenRenameFolderModal && (
        <RenameFolderModal
          isOpen={isOpenRenameFolderModal}
          onClose={onCloseRenameFolderModal}
        />
      )}

      {isOpenRenameMediaCollectionModal && (
        <RenameMediaCollectionModal
          isOpen={isOpenRenameMediaCollectionModal}
          onClose={onCloseRenameMediaCollectionModal}
        />
      )}

      {isOpenDownloadModal && (
        <DownloadModal
          isOpen={isOpenDownloadModal}
          onClose={onCloseDownloadModal}
        />
      )}

      {isOpenDeleteMediaModal && (
        <DeleteMediaModal
          isOpen={isOpenDeleteMediaModal}
          onClose={onCloseDeleteMediaModal}
        />
      )}

      {isOpenTagsModal && (
        <TagsModal
          operation={tagOperation}
          isOpen={isOpenTagsModal}
          onClose={onCloseTagsModal}
        />
      )}

      {isOpenAutoTagsModal && (
        <AutoTagsModal
          fileId={selectedFiles[0]?.fileId}
          isOpen={isOpenAutoTagsModal}
          onClose={onCloseAutoTagsModal}
          getFileDetails={refetchFiles}
        />
      )}

      {isOpenCopyMoveDrawer && (
        <CopyMoveDrawer
          operation={copyMoveOperation}
          isOpen={isOpenCopyMoveDrawer}
          onClose={onCloseCopyMoveDrawer}
        />
      )}

      {!isPublicLink && isOpenFolderDetailsDrawer && (
        <FolderDetailsDrawer
          clientNumber={user?.clientNumber ?? clientInfo?.clientNumber}
          folder={selectedFolders[0]}
          isOpen={isOpenFolderDetailsDrawer}
          onClose={onCloseFolderDetailsDrawer}
        />
      )}

      {isOpenCollectionDetailsDrawer && (
        <MediaCollectionDetailDrawer
          clientNumber={user?.clientNumber}
          isOpen={isOpenCollectionDetailsDrawer}
          mediaCollection={selectedMediaCollections[0]}
          onClose={onCloseCollectionDetailsDrawer}
        />
      )}

      <CustomFoucsAreaModal
        isOpen={isOpenCustomFoucsAreaModal}
        onClose={onCloseCustomFoucsAreaModal}
        fileDetails={selectedFiles[0]}
        getFileDetails={refetchFiles}
      />

      {isOpenDeleteFileVersionModal && (
        <DeleteFileVersionModal
          isOpen={isOpenDeleteFileVersionModal}
          onClose={onCloseDeleteFileVersionModal}
        />
      )}

      {isOpenRestoreFileVersionModal && (
        <RestoreFileVersionModal
          isOpen={isOpenRestoreFileVersionModal}
          onClose={onCloseRestoreFileVersionModal}
        />
      )}

      {isOpenRemoveBgModal && (
        <RemoveBgModal
          isOpen={isOpenRemoveBgModal}
          onClose={onCloseRemoveBgModal}
          fileId={selectedFiles[0]?.fileId}
        />
      )}

      {isOpenDeleteMediaCollectionModal && (
        <DeleteMediaCollectionModal
          isOpen={isOpenDeleteMediaCollectionModal}
          onClose={onCloseDeleteMediaCollectionModal}
        />
      )}

      {isOpenShareAssetModal && (
        <ShareAssetModal
          isOpen={isOpenShareAssetModal}
          onClose={onCloseShareAssetModal}
          assetId={selectedFiles[0]?.fileId ?? selectedFolders[0]?.folderId}
          assetName={selectedFiles[0]?.name ?? selectedFolders[0]?.name}
          assetType={selectedFiles[0]?.type ?? selectedFolders[0]?.type}
        />
      )}

      {isOpenShareMediaCollectionModal && (
        <ShareMediaCollectionModal
          isOpen={isOpenShareMediaCollectionModal}
          onClose={onCloseShareMediaCollectionModal}
          assetId={selectedMediaCollections[0]?.mediaCollectionId}
          assetName={selectedMediaCollections[0]?.name}
        />
      )}

      {isOpenPublicLinksModal && (
        <PublicLinksModal
          assetId={
            selectedFiles[0]?.fileId ??
            selectedFolders[0]?.folderId ??
            selectedMediaCollections[0]?.mediaCollectionId
          }
          assetType={
            selectedFiles[0]?.type ??
            selectedFolders[0]?.type ??
            'media-collection'
          }
          assetName={
            selectedFiles[0]?.name ??
            selectedFolders[0]?.name ??
            selectedMediaCollections[0]?.name
          }
          isOpen={isOpenPublicLinksModal}
          onClose={onClosePublicLinkModal}
        />
      )}

      {isOpenPublishFilesModal && (
        <PublishFilesModal
          isOpen={isOpenPublishFilesModal}
          onClose={onClosePublishFilesModal}
        />
      )}

      {selectedFiles && selectedFiles.length && openEmbedModal ? (
        <CopyEmbedURLModal
          file={selectedFiles[0]}
          openEmbedModal={openEmbedModal}
          handleEmbedModal={handleEmbedModal}
        />
      ) : null}

      {isOpenPhotoEditor && <PhotoEditor onClose={onClosePhotoEditor} />}
    </>
  )
}

export default ContextMenu
